import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@saas-kit/ui';
import {
  DataTable,
  type DataTableColumn,
  type PaginationState,
} from '@saas-kit/ui/DataTable';

import type { AdminAuditLog } from './api';
import { AuditMetadataCell } from './AuditMetadataCell';

interface Props {
  logs: AdminAuditLog[];
  pagination: PaginationState;
  isLoading: boolean;
  error: string | null;
}

export function AuditTable({
  logs,
  pagination,
  isLoading,
  error,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation('adminAudit');

  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(i18n.language, {
        dateStyle: 'medium',
        timeStyle: 'medium',
      }),
    [i18n.language],
  );

  const actorTypeLabels = useMemo<Record<string, string>>(
    () => ({
      user: t('actor.user'),
      backoffice: t('actor.backoffice'),
      system: t('actor.system'),
      service: t('actor.service'),
      anonymous: t('actor.anonymous'),
    }),
    [t],
  );

  const columns = useMemo<DataTableColumn<AdminAuditLog>[]>(
    () => [
      {
        id: 'created_at',
        header: t('table.when'),
        cell: (row) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {dateFormatter.format(new Date(row.created_at))}
          </span>
        ),
        exportValue: (row) => row.created_at,
      },
      {
        id: 'actor',
        header: t('table.actor'),
        cell: (row) => (
          <div className="flex flex-col">
            <Badge variant="secondary" className="w-fit">
              {actorTypeLabels[row.actor_type] ?? row.actor_type}
            </Badge>
            <span className="mt-1 break-all font-mono text-[11px] text-muted-foreground">
              {row.actor_id}
            </span>
          </div>
        ),
        exportValue: (row) => `${row.actor_type}:${row.actor_id}`,
      },
      {
        id: 'action',
        header: t('table.action'),
        cell: (row) => <span className="font-mono text-xs">{row.action}</span>,
      },
      {
        id: 'target',
        header: t('table.target'),
        cell: (row) => (
          <div className="flex flex-col">
            <span className="text-xs text-muted-foreground">{row.target_type}</span>
            <span className="break-all font-mono text-[11px]">{row.target_id}</span>
          </div>
        ),
        hideOnMobile: true,
        exportValue: (row) => `${row.target_type}:${row.target_id}`,
      },
      {
        id: 'tenant',
        header: t('table.tenant'),
        cell: (row) =>
          row.tenant_id ? (
            <span className="break-all font-mono text-[11px]">{row.tenant_id}</span>
          ) : (
            <span className="text-xs text-muted-foreground">{t('table.tenant_global')}</span>
          ),
        hideOnMobile: true,
        exportValue: (row) => row.tenant_id ?? '',
      },
      {
        id: 'metadata',
        header: t('table.metadata'),
        cell: (row) => <AuditMetadataCell metadata={row.metadata ?? {}} />,
        exportValue: (row) => JSON.stringify(row.metadata ?? {}),
      },
    ],
    [t, dateFormatter, actorTypeLabels],
  );

  return (
    <DataTable<AdminAuditLog>
      data={logs}
      columns={columns}
      rowKey={(row) => row.id}
      testId="admin-audit"
      selectable
      pagination={pagination}
      isLoading={isLoading}
      error={error}
      emptyText={t('empty.title')}
      exportable
      exportFilename="admin-audit"
    />
  );
}

